import React, {
    useState,
    useEffect,
} from "react";

import {
    Flex,
    Box,
    Text,
} from "@chakra-ui/react";

import {
    EllipsisVertical,
    Pencil,
    EyeIcon,
    Trash2,
} from "lucide-react";

import ReusableTable from "components/Tables/ReusableTable";

import ThreeDotMenu from "components/Menus/ThreeDotMenu";

import GradientButton from "components/Buttons/GradientButton";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchAgentTrainingAction,
    deleteAgentTrainingAction,
    createAgentTrainingAction
} from "../../../state/actions/agentTrainingActions";

import ViewAgentTraining from "./components/ViewAgentTraining";
import EditAgentTraining from "./components/EditAgentTraining";
import AddAgentTraining from "./components/AddAgentTraining";
import DeleteAgentTraining from "./components/DeleteAgentTraining";

import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";

import { formatDate } from "utils/dateFormatter";
import { useCustomNotification } from "../../../components/Toasts/OpenNotification";


const AgentTrainingManagement = () => {

    const dispatch = useDispatch();
    const notify = useCustomNotification();

    const [
        isViewTrainingDrawerOpen,
        setViewTrainingDrawerOpen,
    ] = useState(false);


    const [
        isEditTrainingDrawerOpen,
        setEditTrainingDrawerOpen,
    ] = useState(false);


    const [
        isAddTrainingDrawerOpen,
        setAddTrainingDrawerOpen,
    ] = useState(false);


    const [
        isDeleteTrainingModalOpen,
        setDeleteTrainingModalOpen,
    ] = useState(false);


    const [
        selectedTraining,
        setSelectedTraining,
    ] = useState(null);


    const [
        selectedTrainingToDelete,
        setSelectedTrainingToDelete,
    ] = useState(null);


    const [
        currentPage,
        setCurrentPage,
    ] = useState(1);


    const PAGE_SIZE = 10;


    const {
        training,
        loading,
        error,
        hasMore,
        total,
    } = useSelector(
        (state) =>
            state.agentTraining
    );



    useEffect(() => {

        dispatch(
            fetchAgentTrainingAction(
                currentPage,
                PAGE_SIZE
            )
        );

    }, [
        dispatch,
        currentPage,
    ]);


    const handleViewTraining = (
        training
    ) => {

        setSelectedTraining(
            training
        );

        setViewTrainingDrawerOpen(
            true
        );
    };


    const handleCloseViewTrainingDrawer =
        () => {

            setSelectedTraining(
                null
            );

            setViewTrainingDrawerOpen(
                false
            );
        };


    const handleOpenEditTrainingDrawer =
        (training) => {

            setSelectedTraining(
                training
            );

            setEditTrainingDrawerOpen(
                true
            );
        };


    const handleCloseEditTrainingDrawer =
        () => {

            setSelectedTraining(
                null
            );

            setEditTrainingDrawerOpen(
                false
            );
        };



    const handleOpenAddTrainingDrawer =
        () => {

            setAddTrainingDrawerOpen(
                true
            );
        };


    const handleCloseAddTrainingDrawer =
        () => {

            setAddTrainingDrawerOpen(
                false
            );
        };

        const handleAddTraining = async (formData) => {
    try {

        const response = await dispatch(
            createAgentTrainingAction(formData)
        );

        if (response?.success) {

            notify({
                message: "Training Created",
                description:
                    "Agent training was created successfully.",
                type: "success",
            });

            setAddTrainingDrawerOpen(false);

            dispatch(
                fetchAgentTrainingAction(
                    1,
                    10
                )
            );

        } else {

            notify({
                message: "Failed to Create Training",
                description:
                    response?.error ||
                    "Something went wrong while creating the training.",
                type: "error",
            });

        }

        return response;

    } catch (error) {

        console.error(
            "Error creating training:",
            error
        );

        notify({
            message: "Error Creating Training",
            description:
                error.message ||
                "Something went wrong while creating the training.",
            type: "error",
        });

        return {
            success: false,
            error:
                error.message ||
                "Failed to create training",
        };
    }
};


    const handleDeleteTraining = (
        training
    ) => {

        if (!training?.training_id) {
            return;
        }


        setSelectedTrainingToDelete(
            training
        );


        setDeleteTrainingModalOpen(
            true
        );
    };



    const handleCloseDeleteTrainingModal =
        () => {

            setDeleteTrainingModalOpen(
                false
            );

            setSelectedTrainingToDelete(
                null
            );
        };



    const confirmDeleteTraining =
        async () => {

            if (
                !selectedTrainingToDelete
                    ?.training_id
            ) {
                return;
            }


            const response =
                await dispatch(
                    deleteAgentTrainingAction(
                        selectedTrainingToDelete
                            .training_id
                    )
                );


            if (response?.success) {

                dispatch(
                    fetchAgentTrainingAction(
                        currentPage,
                        PAGE_SIZE
                    )
                );

            }


            handleCloseDeleteTrainingModal();
        };


    const generateMenuItems = (
        row
    ) => [

        {
            icon:
                <EyeIcon
                    size={16}
                />,

            text:
                "View Training",

            onClick: () =>
                handleViewTraining(
                    row.original
                ),
        },


        {
            icon:
                <Pencil
                    size={16}
                />,

            text:
                "Edit Training",

            onClick: () =>
                handleOpenEditTrainingDrawer(
                    row.original
                ),
        },


        {
            icon:
                <Trash2
                    size={16}
                />,

            text:
                "Delete Training",

            onClick: () =>
                handleDeleteTraining(
                    row.original
                ),
        },

    ];


    const columnsData = [

        {
            Header:
                "Title",

            accessor:
                (row) =>
                    row.title ||
                    "N/A",
        },


        {
            Header:
                "Description",

            accessor:
                (row) =>
                    row.description ||
                    "N/A",
        },


        {
            Header:
                "Mandatory",

            accessor:
                (row) =>
                    row.mandatory
                        ? "Yes"
                        : "No",

            Cell:
                ({
                    value,
                }) => (

                    <StatusBadge
                        label={value}
                        variant={
                            value === "Yes"
                                ? "success"
                                : "gray"
                        }
                    />

                ),
        },


        {
            Header:
                "Status",

            accessor:
                (row) =>
                    row.status ||
                    "Unknown",

            Cell:
                ({
                    value,
                }) => (

                    <StatusBadge
                        label={value}
                        variant={
                            getStatusVariant(
                                value
                            )
                        }
                    />

                ),
        },


        {
            Header:
                "Display Order",

            accessor:
                (row) =>
                    row.display_order ??
                    "N/A",
        },


        {
            Header:
                "Created",

            accessor:
                (row) =>
                    row.created_at,

            Cell:
                ({
                    value,
                }) =>
                    value
                        ? formatDate(
                            value
                        )
                        : "N/A",
        },


        {
            Header:
                "Actions",

            accessor:
                "moreOptions",

            Cell:
                ({
                    row,
                }) => (

                    <ThreeDotMenu

                        icon={
                            <EllipsisVertical
                                size={20}
                                color="grey"
                            />
                        }

                        items={
                            generateMenuItems(
                                row
                            )
                        }

                    />

                ),
        },

    ];



    const tableData =
        Array.isArray(
            training?.data
        )
            ? training.data
            : [];



    return (

        <Flex
            flexDirection="column"
            pt={{
                base: "120px",
                md: "25px",
            }}
        >

            {/* HEADER */}

            <Box p={6}>

                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                >

                    <Text
                        fontSize="md"
                        fontWeight="bold"
                    >

                        {loading ? (

                            <Skeleton
                                height="20px"
                                width="100px"
                            />

                        ) : (

                            `All Agent Trainings: ${
                                total ??
                                training?.total ??
                                0
                            }`

                        )}

                    </Text>


                    <GradientButton
                        onClick={
                            handleOpenAddTrainingDrawer
                        }
                        height="40px"
                    >
                        Add Training
                    </GradientButton>

                </Flex>

            </Box>


            {/* TABLE */}

            <ReusableTable

                columnsData={
                    columnsData
                }

                tableData={
                    tableData
                }

                isLoading={
                    loading
                }

                error={
                    error
                }

                emptyMessage={
                    "No agent training materials found."
                }

            />


            {/* PAGINATION */}

            <Box
                mt={4}
                display="flex"
                justifyContent="center"
                gap={2}
            >

                <GradientButton

                    onClick={() =>
                        setCurrentPage(
                            (prev) =>
                                Math.max(
                                    prev - 1,
                                    1
                                )
                        )
                    }

                    disabled={
                        currentPage === 1
                    }

                    height="30px"
                >
                    Previous
                </GradientButton>


                <Text
                    alignSelf="center"
                >
                    Page{" "}
                    {
                        currentPage
                    }
                </Text>


                <GradientButton

                    onClick={() =>
                        setCurrentPage(
                            (prev) =>
                                prev + 1
                        )
                    }

                    disabled={
                        !hasMore
                    }

                    height="30px"
                >
                    Next
                </GradientButton>

            </Box>


            {/* VIEW TRAINING */}

            <ReusableDrawer

                isOpen={
                    isViewTrainingDrawerOpen
                }

                onClose={
                    handleCloseViewTrainingDrawer
                }

                headerContent={
                    <h2>
                        View Agent Training
                    </h2>
                }

                bodyContent={

                    selectedTraining && (

                        <ViewAgentTraining
                            training={
                                selectedTraining
                            }
                        />

                    )

                }

            />


            {/* EDIT TRAINING */}

            <EditAgentTraining

                isOpen={
                    isEditTrainingDrawerOpen
                }

                onClose={
                    handleCloseEditTrainingDrawer
                }

                training={
                    selectedTraining
                }

            />


            {/* ADD TRAINING */}

            <AddAgentTraining

                isOpen={
                    isAddTrainingDrawerOpen
                }

                onClose={
                    handleCloseAddTrainingDrawer
                }
                 onAddTraining={handleAddTraining}

            />


            {/* DELETE CONFIRMATION */}

            <DeleteAgentTraining

                isOpen={
                    isDeleteTrainingModalOpen
                }

                onClose={
                    handleCloseDeleteTrainingModal
                }

                onConfirm={
                    confirmDeleteTraining
                }

                trainingTitle={
                    selectedTrainingToDelete?.title ||
                    "this training material"
                }

            />

        </Flex>

    );

};


export default AgentTrainingManagement;